import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { DollarSign, TrendingUp, Clock, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { EarningsSummaryCard } from './components/EarningsSummaryCard';
import { EarningsFilters } from './components/EarningsFilters';
import { EarningsListItem } from './components/EarningsListItem';
import { EarningsChart } from './components/EarningsChart';
import { earningsService } from '../../../services/earningsService';

export default function ContractorEarningsTracker() {
  const [earnings, setEarnings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all',
    companyId: '',
    paymentType: 'all'
  });
  const [chartPeriod, setChartPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!loading) {
      loadEarnings();
    }
  }, [filters]);

  useEffect(() => {
    if (!loading) {
      loadChartData();
    }
  }, [chartPeriod]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      const [summaryData, companiesData, earningsData, chartData] = await Promise.all([
        earningsService?.getEarningsSummary(),
        earningsService?.getContractorCompanies(),
        earningsService?.getContractorEarnings(filters),
        earningsService?.getEarningsChartData(chartPeriod)
      ]);

      setSummary(summaryData);
      setCompanies(companiesData);
      setEarnings(earningsData);
      setChartData(chartData);
    } catch (err) {
      console.error('Error loading earnings data:', err);
      setError(err?.message || 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const loadEarnings = async () => {
    try {
      setError('');
      const data = await earningsService?.getContractorEarnings(filters);
      setEarnings(data);
    } catch (err) {
      console.error('Error loading earnings:', err);
      setError(err?.message || 'Failed to load earnings');
    }
  };

  const loadChartData = async () => {
    try {
      const data = await earningsService?.getEarningsChartData(chartPeriod);
      setChartData(data);
    } catch (err) {
      console.error('Error loading chart data:', err);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Helmet>
          <title>Earnings Tracker - Mervo Contractor</title>
        </Helmet>
        <Header />
        <div className="flex flex-1">
          <Sidebar />
          <main className="flex-1 p-8">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <p className="text-text-secondary">Loading earnings data...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Earnings Tracker - Mervo Contractor</title>
      </Helmet>
      <Header />
      <div className="flex flex-1">
        <Sidebar />

        <main className="flex-1 p-4 sm:p-8 overflow-y-auto ml-0 lg:ml-60 pt-16">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">Earnings Tracker</h1>
            <p className="text-text-secondary">Track your earnings, view detailed reports, and analyze your financial performance</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <EarningsSummaryCard
              title="Total Earnings"
              amount={summary?.totalEarnings || 0}
              change={null}
              period="All Time"
              icon={DollarSign}
              trend="up"
            />
            <EarningsSummaryCard
              title="Monthly Earnings"
              amount={summary?.monthlyEarnings || 0}
              change={12}
              period="This Month"
              icon={TrendingUp}
              trend="up"
            />
            <EarningsSummaryCard
              title="Approved Earnings"
              amount={summary?.approvedEarnings || 0}
              change={null}
              period={`${summary?.totalJobs || 0} Jobs`}
              icon={CheckCircle}
              trend="up"
            />
            <EarningsSummaryCard
              title="Total Hours"
              amount={summary?.totalHours || 0}
              change={null}
              period="All Time"
              icon={Clock}
              trend="up"
            />
          </div>

          {/* Chart Section */}
          <div className="mb-8">
            <div className="flex items-center justify-end gap-2 mb-4">
              {['week', 'month', 'year']?.map((period) => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    chartPeriod === period
                      ? 'bg-primary-orange text-white' :'bg-surface-light text-text-secondary hover:bg-surface'
                  }`}
                >
                  {period?.charAt(0)?.toUpperCase() + period?.slice(1)}
                </button>
              ))}
            </div>
            <EarningsChart data={chartData} period={chartPeriod} />
          </div>

          {/* Filters */}
          <div className="mb-8">
            <EarningsFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              companies={companies}
            />
          </div>

          {/* Earnings List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-text-primary">
                Earnings History ({earnings?.length})
              </h2>
            </div>

            {earnings?.length === 0 ? (
              <div className="bg-surface p-12 rounded-xl border border-border text-center">
                <DollarSign className="w-16 h-16 text-text-secondary mx-auto mb-4 opacity-50" />
                <p className="text-text-secondary text-lg">No earnings found</p>
                <p className="text-text-secondary text-sm mt-2">
                  Try adjusting your filters or complete more jobs to see earnings
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {earnings?.map((earning) => (
                  <EarningsListItem key={earning?.id} earning={earning} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}